import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TopbarSearch } from '@/components/ui/TopbarSearch';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { CardList } from '@/components/ui/CardList';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useTemplates } from './hooks/useTemplates';
import { cn } from '@/utils/cn';
import { FileText, Plus, Search, MoreVertical, Layout, Settings } from 'lucide-react';

export function TemplatesListPage() {
  const navigate = useNavigate();
  const { templates, filters, isLoading, setFilters, deleteTemplate } = useTemplates();
  const [deleteId, setDeleteId] = React.useState<string | null>(null);

  const statusMap = {
    active: { label: 'Công khai', color: 'bg-emerald-500', text: 'text-emerald-600' },
    archived: { label: 'Lưu trữ', color: 'bg-slate-400', text: 'text-slate-500' },
    draft: { label: 'Nháp', color: 'bg-amber-500', text: 'text-amber-600' },
  };

  const typeMap = {
    report: 'Báo cáo',
    workflow: 'Quy trình',
    form: 'Biểu mẫu',
    other: 'Khác',
  };

  return (
    <div className="space-y-4">
      {/* Header & Stats */}
      <div className="rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <h1 className="text-sm font-medium text-slate-900">Mẫu tài liệu</h1>
            <p className="text-xs text-slate-500">Quản lý và tùy chỉnh các mẫu báo cáo, quy trình vận hành.</p>
          </div>
          <button
            onClick={() => navigate('?tab=templates-new')}
            className="flex h-10 items-center justify-center gap-2 rounded-md bg-emerald-700 px-4 text-xs font-medium text-white transition-all hover:bg-emerald-800 active:scale-95 shadow-md shadow-emerald-700/10"
          >
            <Plus className="h-4 w-4" />
            <span>Tạo mẫu mới</span>
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="rounded-md border border-slate-100 bg-white p-3 shadow-sm flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-row items-center gap-2 flex-1">
          <div className="flex-1 min-w-0">
            <TopbarSearch
              placeholder="Tìm theo tên hoặc mã mẫu..."
              value={filters.query}
              onChange={(query) => setFilters({ ...filters, query })}
            />
          </div>
        </div>
        <div className="flex flex-row items-center gap-2 overflow-x-auto pb-1 md:pb-0">
          {(['all', 'report', 'workflow', 'form'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setFilters({ ...filters, type })}
              className={cn(
                'shrink-0 whitespace-nowrap rounded-md px-3 h-10 text-xs font-medium transition-all flex items-center',
                filters.type === type
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                  : 'bg-slate-50 text-slate-500 border border-transparent hover:bg-slate-100'
              )}
            >
              {type === 'all' ? 'Tất cả' : typeMap[type as keyof typeof typeMap]}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden rounded-md border border-slate-100 bg-white shadow-sm overflow-hidden md:block">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-50 bg-slate-50/50 text-xs font-medium text-slate-500">
              <th className="p-3 pl-6 font-normal">Mẫu tài liệu</th>
              <th className="p-3 text-center font-normal">Phân loại</th>
              <th className="p-3 text-center font-normal">Lượt dùng</th>
              <th className="p-3 text-center font-normal">Trạng thái</th>
              <th className="p-3 pr-6 text-right font-normal">Thao tác</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td colSpan={5} className="p-4"><div className="h-10 rounded-md bg-slate-50" /></td>
                </tr>
              ))
            ) : templates.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-10 text-center text-slate-400">Không tìm thấy mẫu tài liệu nào</td>
              </tr>
            ) : (
              templates.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-3 pl-6">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate">{item.name}</p>
                        <p className="text-[10px] font-medium text-slate-400">{item.code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <span className="rounded-md bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-600">
                      {typeMap[item.type as keyof typeof typeMap]}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <span className="font-medium text-slate-700">{item.usageCount}</span>
                  </td>
                  <td className="p-3 text-center">
                    <span className={cn('inline-flex items-center gap-1.5 font-medium text-[10px]', statusMap[item.status].text)}>
                      <div className={cn('h-1.5 w-1.5 rounded-full', statusMap[item.status].color)} />
                      {statusMap[item.status].label}
                    </span>
                  </td>
                  <td className="p-3 pr-6 text-right">
                    <ActionMenu
                      options={[
                        { label: 'Chi tiết', icon: 'ri-eye-line', onClick: () => navigate(`?tab=templates-detail&id=${item.id}`) },
                        { label: 'Chỉnh sửa', icon: 'ri-edit-line', onClick: () => navigate(`?tab=templates-edit&id=${item.id}`) },
                        { label: 'Lưu trữ', icon: 'ri-archive-line', onClick: () => {} },
                        { label: 'Xóa', icon: 'ri-delete-bin-line', variant: 'danger', onClick: () => setDeleteId(item.id) },
                      ]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card List */}
      <div className="md:hidden">
        <CardList
          items={templates}
          isLoading={isLoading}
          emptyMessage="Không tìm thấy mẫu tài liệu nào"
          renderCard={(item) => (
            <div key={item.id} className="rounded-md border border-slate-100 bg-white p-3 shadow-sm space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate">{item.name}</p>
                    <p className="text-[10px] font-medium text-slate-400">{item.code}</p>
                  </div>
                </div>
                <ActionMenu
                  options={[
                    { label: 'Chi tiết', icon: 'ri-eye-line', onClick: () => navigate(`?tab=templates-detail&id=${item.id}`) },
                    { label: 'Chỉnh sửa', icon: 'ri-edit-line', onClick: () => navigate(`?tab=templates-edit&id=${item.id}`) },
                    { label: 'Xóa', icon: 'ri-delete-bin-line', variant: 'danger', onClick: () => setDeleteId(item.id) },
                  ]}
                />
              </div>
              <div className="flex items-center justify-between text-[11px] font-medium pt-2 border-t border-slate-50">
                <div className="flex gap-2">
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-600">{typeMap[item.type as keyof typeof typeMap]}</span>
                  <span className="text-slate-400">Dùng: {item.usageCount}</span>
                </div>
                <span className={cn('inline-flex items-center gap-1.5', statusMap[item.status].text)}>
                  <div className={cn('h-1.5 w-1.5 rounded-full', statusMap[item.status].color)} />
                  {statusMap[item.status].label}
                </span>
              </div>
            </div>
          )}
        />
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteId}
        title="Xác nhận xóa"
        description="Bạn có chắc chắn muốn xóa mẫu tài liệu này? Hành động này không thể hoàn tác."
        confirmLabel="Xóa ngay"
        cancelLabel="Hủy"
        variant="danger"
        onConfirm={() => {
          if (deleteId) deleteTemplate(deleteId);
          setDeleteId(null);
        }}
        onClose={() => setDeleteId(null)}
      />
    </div>
  );
}
